import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import {
  POLL_FEEDBACK_MS,
  POLL_MACHINE_MS,
  clampWhenHidden,
} from "@/lib/realtime";
import type {
  WorkshopAgentDefinition,
  WorkshopArtifactListItem,
  WorkshopDeployment,
  WorkshopDeploymentMessage,
  WorkshopDeploymentRun,
} from "@/types/workshop.type";
import { isDeploymentRunBusy } from "@/types/workshop.type";
import {
  workshopAgentsKey,
  workshopDeploymentArtifactsKey,
  workshopDeploymentMessagesKey,
  workshopDeploymentRunsKey,
  workshopDeploymentsKey,
} from "./workshopKeys";

/**
 * Machine tier while any run is queued/running; otherwise the feedback tier
 * so a just-finished run's summary can land. Never false: deployments are not
 * a terminal surface.
 */
export function deploymentsPollMs(
  deployments: WorkshopDeployment[] | undefined,
): number {
  if (deployments?.some((d) => isDeploymentRunBusy(d.lastRunStatus))) {
    return POLL_MACHINE_MS;
  }
  return POLL_FEEDBACK_MS;
}

export function useWorkshopAgents() {
  return useQuery({
    queryKey: workshopAgentsKey,
    queryFn: async () => {
      const res = await axiosInstance.get<{ agents: WorkshopAgentDefinition[] }>(
        `/workshop/agents`,
      );
      return res.data.agents;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useWorkshopDeployments(projectId: string | null) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: workshopDeploymentsKey(projectId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ deployments: WorkshopDeployment[] }>(
        `/workshop/projects/${projectId}/deployments`,
      );
      return res.data.deployments;
    },
    enabled: !!projectId,
    refetchInterval: (q) => clampWhenHidden(deploymentsPollMs(q.state.data)),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: workshopDeploymentsKey(projectId) });
  };

  const createDeployment = useMutation({
    mutationFn: async (vars: {
      definitionId: string;
      name?: string;
      objective: string;
    }) => {
      const res = await axiosInstance.post<{
        deploymentId: string;
        runId: string | null;
        nextRunAt: string | null;
      }>(`/workshop/projects/${projectId}/deployments`, {
        ...vars,
        mode: "once",
      });
      return res.data;
    },
    onSuccess: invalidate,
  });

  const deleteMutationKey = ["workshopDeleteDeployment", projectId];
  const deleteDeployment = useMutation({
    mutationKey: deleteMutationKey,
    mutationFn: async (deploymentId: string) => {
      await axiosInstance.delete(`/workshop/deployments/${deploymentId}`);
    },
    onMutate: async (deploymentId) => {
      const key = workshopDeploymentsKey(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<WorkshopDeployment[]>(key);
      const index = previous?.findIndex((d) => d.id === deploymentId) ?? -1;
      const removed = index >= 0 ? previous![index] : null;
      qc.setQueryData<WorkshopDeployment[]>(key, (list) =>
        list?.filter((d) => d.id !== deploymentId),
      );
      return { removed, index };
    },
    onError: (error, _deploymentId, context) => {
      const removed = context?.removed;
      if (removed) {
        qc.setQueryData<WorkshopDeployment[]>(
          workshopDeploymentsKey(projectId),
          (list = []) => {
            if (list.some((d) => d.id === removed.id)) return list;
            const next = [...list];
            next.splice(Math.min(context.index, next.length), 0, removed);
            return next;
          },
        );
      }
      queue.add({
        title: "Couldn't delete the agent",
        description: getAiRequestErrorMessage(error),
        variant: "error",
      });
    },
    // Refetching while another delete is in flight would bring its row back.
    onSettled: (_data, _error, deploymentId) => {
      if (qc.isMutating({ mutationKey: deleteMutationKey }) > 1) return;
      invalidate();
      void qc.invalidateQueries({ queryKey: workshopDeploymentRunsKey(deploymentId) });
      void qc.invalidateQueries({ queryKey: workshopDeploymentArtifactsKey(deploymentId) });
      void qc.invalidateQueries({ queryKey: workshopDeploymentMessagesKey(deploymentId) });
    },
  });

  return {
    deployments: query.data ?? [],
    isLoading: query.isLoading,
    /** The read failed and nothing was ever read: `deployments` is not a real empty list. */
    isError: query.isError && query.data === undefined,
    refetch: query.refetch,
    createDeployment,
    deleteDeployment,
  };
}

export function useWorkshopDeploymentRuns(
  deploymentId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: workshopDeploymentRunsKey(deploymentId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ runs: WorkshopDeploymentRun[] }>(
        `/workshop/deployments/${deploymentId}/runs`,
      );
      return res.data.runs;
    },
    enabled: !!deploymentId && enabled,
    refetchInterval: (q) =>
      clampWhenHidden(
        q.state.data?.some((r) => isDeploymentRunBusy(r.status))
          ? POLL_MACHINE_MS
          : POLL_FEEDBACK_MS,
      ),
  });
}

export function useWorkshopDeploymentArtifacts(
  deploymentId: string | null,
  enabled: boolean,
  /** Poll while the run is still in flight so new artifacts appear. */
  pollWhileBusy = false,
) {
  return useQuery({
    queryKey: workshopDeploymentArtifactsKey(deploymentId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ artifacts: WorkshopArtifactListItem[] }>(
        `/workshop/deployments/${deploymentId}/artifacts`,
      );
      return res.data.artifacts;
    },
    enabled: !!deploymentId && enabled,
    refetchInterval: () =>
      clampWhenHidden(pollWhileBusy ? POLL_MACHINE_MS : false),
  });
}

/** The agent's conversation, oldest first. Polls while it works so new turns land. */
export function useWorkshopDeploymentMessages(
  deploymentId: string | null,
  enabled: boolean,
  pollWhileBusy = false,
) {
  return useQuery({
    queryKey: workshopDeploymentMessagesKey(deploymentId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ messages: WorkshopDeploymentMessage[] }>(
        `/workshop/deployments/${deploymentId}/messages`,
      );
      return res.data.messages;
    },
    enabled: !!deploymentId && enabled,
    refetchInterval: () =>
      clampWhenHidden(pollWhileBusy ? POLL_MACHINE_MS : false),
  });
}
